import {
  IconArrowBarToDown,
  IconArrowBarToUp,
  IconHeart,
  IconLoader2,
  IconRefresh,
  IconUserPlus,
} from "@tabler/icons-react";
import {Spin} from "@ui";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import useGetUserInfo from "@/hooks/useGetUserInfo";
import useGetUserWallet from "@/hooks/wallet/useGetUserWallet";
import {useBalanceStore} from "@/store/useBalance.store";
import useCurrencyStore from "@/store/useCurrency.store";
import {getUserAvatarImage, getUserInitials} from "@/util/avatarUtils";
import {formatCurrencyAmount} from "@/util/balance.util";
import styles from "./SidebarUserCard.module.css";

const SidebarUserCard = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [isSpinning, setIsSpinning] = useState(false);
  const [imageError, setImageError] = useState(false);

  const {data: userData, isLoading: userLoading} = useGetUserInfo();
  const {isLoading: walletLoading, balance, getAccountByCurrency, refetch} = useGetUserWallet();
  const {selectedCurrency} = useCurrencyStore();

  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);
  const handleWithdrawalModal = useBalanceStore(state => state.handleWithdrawalModal);

  const userInitials = getUserInitials(userData);
  const avatarImage = getUserAvatarImage(userData);

  const selectedAccount = selectedCurrency ? getAccountByCurrency(selectedCurrency.id) : balance;
  const accountToDisplay = selectedAccount || balance;

  const displayName = userData?.alias
    ? `@${userData.alias}`
    : userData?.given_name || userData?.firstName || userData?.email?.split("@")[0] || "";

  const handleRefresh = () => {
    setIsSpinning(true);
    refetch();
    setTimeout(() => setIsSpinning(false), 700);
  };

  const formatBalance = () => {
    if (walletLoading) {
      return (
        <Spin
          indicator={
            <IconLoader2
              size={14}
              className="icon-spin"
            />
          }
          size="small"
        />
      );
    }
    return (
      formatCurrencyAmount(accountToDisplay?.balance, selectedCurrency?.id || accountToDisplay?.currency || "VES") ||
      "Bs. 0,00"
    );
  };

  return (
    <div className={styles.userCard}>
      {/* Profile */}
      <div className={styles.profileRow}>
        <div className={styles.avatarWrapper}>
          {userLoading ? (
            <div className={styles.avatarInitials} />
          ) : !imageError && avatarImage ? (
            <img
              src={avatarImage}
              alt=""
              className={styles.avatarImg}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className={styles.avatarInitials}>{userInitials}</div>
          )}
        </div>
        <div className={styles.userInfo}>
          <div className={styles.userName}>{displayName}</div>
          {userData?.email && <div className={styles.userEmail}>{userData.email}</div>}
        </div>
      </div>

      {/* Balance */}
      <div className={styles.balanceRow}>
        <div>
          <div className={styles.balanceLabel}>{t("homeBtnMoneyHandler_balance")}</div>
          <div className={styles.balanceValue}>{formatBalance()}</div>
        </div>
        <button
          type="button"
          className={styles.refreshBtn}
          onClick={handleRefresh}
          aria-label="Actualizar saldo"
        >
          <IconRefresh
            size={14}
            className={isSpinning ? styles.spinning : ""}
          />
        </button>
      </div>

      {/* Deposit / Withdraw */}
      <div className={styles.actionsRow}>
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.depositBtn}`}
          onClick={handleBankAccountModal}
        >
          <IconArrowBarToDown
            size={14}
            stroke={2.5}
            color="#0f1115"
          />
          {t("homeBtnMoneyHandler_deposit")}
        </button>
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.withdrawBtn}`}
          onClick={handleWithdrawalModal}
        >
          <IconArrowBarToUp
            size={14}
            stroke={2.5}
          />
          {t("homeBtnMoneyHandler_withdraw")}
        </button>
      </div>

      {/* Invite */}
      <button
        type="button"
        className={styles.inviteBtn}
        onClick={() => navigate("/user/referrals")}
      >
        <IconUserPlus size={13} />
        {t("inviteFriend")}
      </button>

      {/* Favorites */}
      <button
        type="button"
        className={styles.favoritesBtn}
        onClick={() => {
          navigate("/favorites");
        }}
      >
        <IconHeart size={13} />
        {t("sidebarMyFavorites")}
      </button>
    </div>
  );
};

export default SidebarUserCard;
